import { useState } from "react";
import { motion } from "framer-motion";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Zap, Rocket, Gem, Wand2, Coins } from "lucide-react";
import { PayPalBuy, PAYPAL_OPTIONS } from "@/components/PayPalBuy";

const PACKS = [
  { id: "p10", credits: 10, icon: Zap, tag: "Starter" },
  { id: "p50", credits: 50, icon: Rocket, tag: "Popular" },
  { id: "p100", credits: 100, icon: Gem, tag: "Best Value" },
  { id: "custom", credits: null, icon: Wand2, tag: "Custom" },
];

const priceFor = (credits) => (credits * 0.05).toFixed(2);

export const TopupModal = ({ open, onOpenChange, onStatusChange }) => {
  const [selected, setSelected] = useState("p100");
  const [customCredits, setCustomCredits] = useState(250);

  const rawCustom = parseInt(customCredits, 10) || 0;
  const effectiveCredits = selected === "custom" ? rawCustom : PACKS.find((p) => p.id === selected).credits;
  const validCustom = selected !== "custom" || (rawCustom >= 10 && rawCustom <= 10000);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="topup-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            <Coins size={22} className="text-amber-600" /> Top Up Credits
          </DialogTitle>
          <DialogDescription>1 credit = 1 domain scanned · $0.05 per credit · purchased credits never expire.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
          {PACKS.map((p, i) => {
            const Icon = p.icon;
            const isSel = selected === p.id;
            return (
              <motion.button
                key={p.id}
                data-testid={`pack-${p.id}`}
                initial={{ opacity: 0, y: 18, scale: 0.92 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: i * 0.08, type: "spring", stiffness: 260, damping: 20 }}
                whileHover={{ y: -5, scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => setSelected(p.id)}
                className={`relative flex flex-col items-center gap-1.5 p-4 rounded-xl border-2 text-center transition-colors ${
                  isSel ? "border-[#C0392B] bg-[#FADBD8]/30 shadow-[0_4px_14px_0_rgba(192,57,43,0.2)]" : "border-gray-200 bg-white hover:border-gray-300"
                }`}
              >
                <span className={`text-[10px] font-bold uppercase tracking-wider ${isSel ? "text-[#C0392B]" : "text-gray-400"}`}>{p.tag}</span>
                <Icon size={22} className={isSel ? "text-[#C0392B]" : "text-gray-400"} />
                <span className="text-xl font-black text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {p.credits === null ? "?" : p.credits}
                </span>
                <span className="text-xs text-gray-500">{p.credits === null ? "you choose" : `credits · $${priceFor(p.credits)}`}</span>
                {isSel && (
                  <motion.span layoutId="pack-check" className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[#C0392B] text-white text-xs flex items-center justify-center font-bold">
                    ✓
                  </motion.span>
                )}
              </motion.button>
            );
          })}
        </div>

        {selected === "custom" && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="flex items-center gap-3 mt-1">
            <Input
              data-testid="custom-credits-input"
              type="number"
              min={10}
              max={10000}
              value={customCredits}
              onChange={(e) => setCustomCredits(e.target.value)}
              className="w-36"
              placeholder="Credits"
            />
            <span className="text-sm text-gray-500">credits (min 10, max 10,000)</span>
          </motion.div>
        )}

        <div className="flex items-center justify-between border-t border-gray-100 pt-4 mt-2">
          <span className="text-sm text-gray-500">Total</span>
          <span className="text-2xl font-black text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="topup-total">
            {validCustom ? `$${priceFor(effectiveCredits)}` : "—"}
          </span>
        </div>

        <div className="mt-1">
          {validCustom ? (
            <PayPalScriptProvider options={PAYPAL_OPTIONS}>
              <PayPalBuy
                product="credits"
                credits={effectiveCredits}
                onSuccess={(client) => {
                  onStatusChange(client);
                  onOpenChange(false);
                }}
              />
            </PayPalScriptProvider>
          ) : (
            <p className="text-sm text-red-500 text-center py-3" data-testid="custom-credits-error">Enter between 10 and 10,000 credits.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
